// @givmo/app-admin — the words on the portal's "Cover processing fees" rows (CAM-BE11, CAM-BE13,
// CAM-R81).
//
// ONE place decides what every cover-fees row says — the company deposit, the Give dialog and the
// approve dialog (one match, or Approve all). The fee line is ruled house-wide (D-CAM-64), for
// every client's row, not only the portal's.
//
// The line states what ticking ADDS, in both states: "Add $0.87 to cover processing fees", the
// server-quoted covered fee, so the increment shows before the choice is made. Approve all's totals
// are an upper bound: "Add up to $4.32 to cover processing fees". The charge line keeps stating the
// total.
//
// The row is always "Cover processing fees" (D-CAM-61): a non-zero Givmo platform fee is covered by
// the same box, and never gets a row or a title of its own. The wallet rail has no processing fee,
// so there the row stays locked, saying why, unless a platform fee applies.

import { formatFeeFigure, isNonZeroMoney } from '../features/settings/wallet-decode';

/** Every cover-fees row's title, whatever parts its fee has (D-CAM-61). */
const TITLE = 'Cover processing fees';

/** The wallet rail's line when there is nothing to cover (the iOS donate sheet's words). */
export const NO_WALLET_FEES_LINE = 'No processing fees for wallet payments';

/** What a row's figure describes, normalized from a gift/match quote or a deposit quote. */
export interface CoverFeesFigures {
  /** What covering adds on top of the gift, every part of the fee included, a decimal string. */
  readonly coveredFee: string;
}

export interface CoverFeesRowCopyInput {
  /** The server's figures, or `null` while there is no quote to state. */
  readonly figures: CoverFeesFigures | null;
  /** Approve all's totals are an upper bound, so its line says "up to". */
  readonly upTo?: boolean;
  /** The rail, which decides what a row with nothing to cover says. */
  readonly rail: 'card' | 'wallet';
}

export interface CoverFeesRowCopy {
  readonly title: string;
  /** The quiet line under the title, or `null` when there is no figure to state. */
  readonly line: string | null;
  /** The line says WHY the row is locked, so it stays legible while the box and title dim. */
  readonly lineIsReason: boolean;
  /** There is no fee to cover at all (the row is locked). */
  readonly nothingToCover: boolean;
}

/**
 * Normalize a gift or match quote: covering adds its covered fee, the processing and any platform
 * part together.
 */
export function chargeQuoteFigures(quote: {
  readonly covered: { readonly fee: string };
}): CoverFeesFigures {
  return { coveredFee: quote.covered.fee };
}

/** Normalize a company deposit quote (a processing fee only: deposits waive the platform fee). */
export function depositQuoteFigures(quote: {
  readonly covered: { readonly processorFee: string };
}): CoverFeesFigures {
  return { coveredFee: quote.covered.processorFee };
}

/** There is no fee to cover at all (the wallet rail with no platform fee): the row locks. */
export function hasNothingToCover(figures: CoverFeesFigures | null): boolean {
  return figures !== null && !isNonZeroMoney(figures.coveredFee);
}

/** What a cover-fees row says for these figures: the same, ticked or not. */
export function coverFeesRowCopy({
  figures,
  upTo = false,
  rail,
}: CoverFeesRowCopyInput): CoverFeesRowCopy {
  if (figures === null) {
    return { title: TITLE, line: null, lineIsReason: false, nothingToCover: false };
  }
  const nothingToCover = hasNothingToCover(figures);
  if (nothingToCover) {
    return rail === 'wallet'
      ? { title: TITLE, line: NO_WALLET_FEES_LINE, lineIsReason: true, nothingToCover }
      : { title: TITLE, line: null, lineIsReason: false, nothingToCover };
  }
  return {
    title: TITLE,
    line: `Add ${upTo ? 'up to ' : ''}${formatFeeFigure(figures.coveredFee)} to cover processing fees`,
    lineIsReason: false,
    nothingToCover,
  };
}
